import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banknote } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro } from '../../nucleo/api';
import type { AcaoItemInicioProps } from '../../nucleo/modulos-web';
import { useSessao } from '../../nucleo/sessao';
import { ModalBaixa } from './ModalBaixa';
import type { Documento } from './tipos';

/**
 * Fase 2 (W21, K7): baixa rápida a partir do card "Próximos vencimentos" do Início (o antigo tinha o diálogo no próprio
 * card). Reusa o `ModalBaixa` do Contas (juros e multa calculados na data, conta padrão, idempotência): busca o documento
 * atual pelo id da linha e abre o mesmo diálogo da ficha. Só aparece com `contas.baixar`; o nome do parceiro no rótulo
 * para leitor de tela já chega pelo modo discreto (o card passa `nomePessoa`). Ao fechar, o Início recarrega (a linha baixada sai do card).
 */
export function BaixaRapida({ itemId, nome, rotulo }: AcaoItemInicioProps) {
  const { t } = useTranslation('contas');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [doc, setDoc] = useState<Documento | null>(null);
  const abrir = useMutation({
    mutationFn: () => get<Documento>(`/contas/documentos/${itemId}`),
    onSuccess: (d) => setDoc(d),
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  if (!s.pode('contas.baixar')) return null;
  const pagar = rotulo === 'conta_pagar';
  return (
    <>
      <Botao
        tamanho="sm"
        variante="fantasma"
        data-baixa-rapida={itemId}
        aria-label={t(pagar ? 'w21.pagarDe' : 'w21.receberDe', { nome })}
        title={t(pagar ? 'w21.pagar' : 'w21.receber')}
        icone={<Banknote className="size-4" aria-hidden />}
        carregando={abrir.isPending}
        onClick={() => abrir.mutate()}
      >
        <span className="sr-only sm:not-sr-only">{t(pagar ? 'w21.pagar' : 'w21.receber')}</span>
      </Botao>
      <ModalBaixa
        doc={doc}
        aoFechar={() => {
          setDoc(null);
          void qc.invalidateQueries({ queryKey: ['inicio'] });
        }}
      />
    </>
  );
}
